import { toast } from 'sonner';
import i18next from 'i18next';

import { usePartyTemplateStore } from '@/store/party-template.store.js';
import { useTenantStore } from '@/store/tenant.store';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class PartyTemplateIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchTemplates(params = {}) {
        const store = usePartyTemplateStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const { columnFilters, ...requestParams } = params;
            const response = await this.service.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const items = (response.data || []).map((it) => ({ ...it, id: it._id || it.id }));
            store.setItems(items);
            if (response.pagination && store.setPaginationData) {
                store.setPaginationData(response.pagination);
            }
            store.setUniqueValues(response.uniqueValues || {});
            return { success: true, data: items, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async resolve({ entityType, code, silent = false } = {}) {
        try {
            const response = await this.service.resolve({ entityType, code });
            return { success: true, data: response.data, source: response.source };
        } catch (error) {
            if (!silent) toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async create(payload) {
        const store = usePartyTemplateStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            const msg = i18next.t('party:messages.tenantRequired', {
                defaultValue: 'Select a tenant from the top menu to create a template.',
            });
            toast.error(msg);
            return { success: false, error: msg };
        }
        store.setLoading(true);
        try {
            const response = await this.service.create({ ...payload, tenant: tenantId });
            toast.success(
                i18next.t('party:messages.createSuccess', { defaultValue: 'Party template created' })
            );
            store.closeForm?.();
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            const code = error?.code;
            const msg =
                code === 'PARTY_TEMPLATE_ENTITY_TYPE_EXISTS'
                    ? i18next.t('party:messages.entityTypeExists', {
                          defaultValue: 'There is already an active party template under the selected tenant for this entity type.',
                      })
                    : getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async cloneToTenant(id) {
        const store = usePartyTemplateStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            const msg = i18next.t('party:page.cloneDisabled.tenantRequired', {
                defaultValue: 'Select a tenant from the top menu to clone a template.',
            });
            toast.error(msg);
            return { success: false, error: msg };
        }

        store.setLoading(true);
        try {
            const response = await this.service.cloneToTenant(id);
            toast.success(
                i18next.t('party:messages.cloneSuccess', {
                    defaultValue: 'Global party template cloned to the selected tenant',
                })
            );
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = error?.code === 'PARTY_TEMPLATE_ENTITY_TYPE_EXISTS'
                ? i18next.t('party:messages.entityTypeExists', {
                      defaultValue: 'An active party template already exists for this entity type in the selected tenant.',
                  })
                : getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, payload) {
        const store = usePartyTemplateStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, payload);
            toast.success(
                i18next.t('party:messages.updateSuccess', { defaultValue: 'Party template updated' })
            );
            store.closeForm?.();
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async publishNewVersion(id, payload) {
        const store = usePartyTemplateStore.getState();
        try {
            const response = await this.service.publishNewVersion(id, payload);
            toast.success(
                i18next.t('party:messages.publishSuccess', { defaultValue: 'New version published' })
            );
            store.closeForm?.();
            await this.fetchTemplates();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        }
    }

    async delete(id) {
        const store = usePartyTemplateStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(i18next.t('party:messages.deleteSuccess', { defaultValue: 'Party template deleted' }));
            store.closeDeleteDialog?.();
            await this.fetchTemplates();
            return { success: true };
        } catch (error) {
            toast.error(getIntegrationErrorMessage(error));
            return { success: false, error: getIntegrationErrorMessage(error) };
        } finally {
            store.setLoading(false);
        }
    }
}
